<template>
  <v-card class="pa-5 overflow-y-hidden d-flex flex-column">
    <v-row
      v-if="reportContextButtonKey"
      no-gutters
      justify="end"
      class="pb-2"
    >
      <v-col cols="auto" class="mr-2">
        <!-- Comment out direct button for now -->
        <!-- <v-btn
          v-if="surveyGroup"
          small
          color="primary"
          :disabled="loadingReport"
          @click="insertSurveyCompletionInfo"
        >
          <v-icon small left>mdi-calendar-check</v-icon>
          {{ $t('analysis.systemPages.evaluationInfo.insertCompletionInfo') }}
        </v-btn> -->
      </v-col>
      <v-col cols="auto">
      <!-- Comment out original context button for now -->
      <!-- <report-context-button
        :value="reportContextButtonKey"
        :disabled="loadingReport"
        @insertRecommendation="handleInsertRecommendedContent"
      /> -->
      <report-context-button
        :value="reportContextButtonKey"
        :disabled="loadingReport"
        :preview-content="surveyCompletionPreviewContent"
        @insertRecommendation="handleInsertPrecomputedContent"
      />
      </v-col>
    </v-row>
    <template v-if="loadingReport">
      <platform-spinner
        class="my-4 mx-auto"
      />
    </template>
    <PlatformWYSIWYG
      v-else
      :placeholder="tooltip"
      :initial-data="currentContent"
      :max-length="textFieldLimit"
      @newInput="saveUpdate"
    />
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { mapActions } from 'vuex'
import { PlatformReport } from '@/types/Report'
import { MaxFieldLengths } from '@/helpers/validation'
import PlatformWYSIWYG from '@/components/PlatformWYSIWYG.vue'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'
import ReportContextButton from '@/components/analysis/ReportContextButton.vue'
import { ReportContextItem } from '@/helpers/recommendations'
import { parseMarkdownContent } from '@/helpers/text'
import { InputFields } from '@betterboards/shared/types/Platform'
import { SurveyGroupProgress } from '@/types/SurveyGroup'
import { generateSurveyCompletionText, generateComparisonDateNoteText } from '@/helpers/surveyGroup'
import { ResultSet } from '@betterboards/shared/types/ResultSet'

export default Vue.extend({
  components: {
    ReportContextButton,
    PlatformSpinner,
    PlatformWYSIWYG
  },
  props: {
    report: { type: Object as PropType<PlatformReport>, required: false },
    loadingReport: { type: Boolean, default: false }
  },
  data: () => ({
    //
  }),
  computed: {
    currentContent (): string | undefined {
      const summary = this.report?.summary
      if (summary?.length) {
        return summary
      }

      return undefined
    },
    tooltip (): string {
      return this.$t('analysis.systemPages.evaluationInfo.placeholder') as string
    },
    reportContextButtonKey (): ReportContextItem | undefined {
      return ReportContextItem.EvaluationInformation
    },
    textFieldLimit (): number {
      const focusAreasFieldOverride: number | undefined = this.report?.configuration?.maxFieldLengths?.[InputFields.EvaluationInformation]
      if (focusAreasFieldOverride) {
        return focusAreasFieldOverride
      }
      return MaxFieldLengths[InputFields.EvaluationInformation]
    },
    // Access survey group completion data
    surveyGroup (): SurveyGroupProgress | undefined {
      return this.$store.getters['Analysis/selectedSurveyGroup']
    },
    surveyGroups (): SurveyGroupProgress[] {
      return this.$store.getters['SurveyGroup/list'] || []
    },
    // Access result set from store (contains comparisonCreatedAt if comparison exists)
    resultSet (): ResultSet | null {
      return this.$store.state.Analysis.resultSet
    },
    analysisDateLocale (): string {
      return this.$store.state.Analysis.variantCode ?? 'en'
    },
    // Precomputed survey completion text for fast insertion (multilingual)
    precomputedSurveyCompletionText (): string {
      if (!this.surveyGroup) {
        return ''
      }

      return generateSurveyCompletionText(
        this.surveyGroup,
        this.analysisDateLocale,
        (key: string, values?: any) => this.$t(key, values) as string
      )
    },
    // Generate comparison date note text if comparison exists
    comparisonDateNoteText (): string | undefined {
      const comparisonCreatedAt = this.resultSet?.comparisonCreatedAt ??
        this.$store.state.Analysis.comparisonResultSet?.createdAt ??
        this.surveyGroups.find((group) => group.id === this.report?.comparisonSurveyGroupId)?.createdAt
      if (!comparisonCreatedAt || !this.resultSet?.createdAt) {
        return undefined
      }

      return generateComparisonDateNoteText(
        comparisonCreatedAt,
        this.resultSet.createdAt,
        this.analysisDateLocale,
        (key: string, values?: any) => this.$t(key, values) as string
      )
    },
    // Override example content with survey completion info and comparison date note
    surveyCompletionPreviewContent (): string | undefined {
      if (!this.surveyGroup) {
        return undefined
      }

      let content = this.precomputedSurveyCompletionText

      // Append comparison date note if comparison exists
      if (this.comparisonDateNoteText) {
        content = content
          ? `${content}\n\n${this.comparisonDateNoteText}`
          : this.comparisonDateNoteText
      }

      return content
    }
  },
  methods: {
    ...mapActions('Report', ['saveReport']),
    async saveUpdate (val) {
      if (!this.report) {
        console.error('no report loaded')
        return
      }
      await this.saveReport({
        id: this.report.id,
        summary: val
      })
    },
    handleInsertRecommendedContent (content: string): void {
      const newContent = this.currentContent
        ? `${this.currentContent}\n\n${content}`
        : content
      this.saveUpdate(
        parseMarkdownContent(newContent)
      )
    },
    // Fast insertion using precomputed text
    handleInsertPrecomputedContent (content: string): void {
      // Prefer preview content (includes comparison note) over precomputed text
      const contentToInsert = content || this.precomputedSurveyCompletionText
      const newContent = this.currentContent
        ? `${this.currentContent}\n\n${contentToInsert}`
        : contentToInsert
      this.saveUpdate(parseMarkdownContent(newContent))
    },
    // Keep original method for direct button click (commented out button)
    insertSurveyCompletionInfo (): void {
      if (!this.surveyGroup) {
        console.warn('No survey group available to insert completion info')
        return
      }
      // Use precomputed text for fast insertion
      const newContent = this.currentContent
        ? `${this.currentContent}\n\n${this.precomputedSurveyCompletionText}`
        : this.precomputedSurveyCompletionText
      this.saveUpdate(parseMarkdownContent(newContent))
    }
  }
})
</script>
